import { useMemo } from 'react';

import { useToast } from '@/components/ui';
import { Combobox } from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';
import { useAddCustomCity, useCustomCities } from '@/hooks/useCustomCities';
import cn from '@/utils/cn';
import { CITIES_BY_STATE, INDIAN_STATES } from '@/utils/indiaStates';

interface StateCitySelectProps {
  state: string;
  city: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
  // Pass false when used inside a modal Dialog so the dropdowns stay scrollable.
  portalled?: boolean;
  // Optional per-cell classes so a parent grid can size the State/City columns
  // independently (e.g. a narrower City next to a wider Address, Fix 1).
  stateClassName?: string;
  cityClassName?: string;
  // Label styling. The lead wizard renders these as small muted sub-labels
  // inside a card; the employee onboarding / profile / admin-user forms need
  // them to match the `Input` component's own label, or the State/City pair
  // reads as a different KIND of field from the ones either side of it.
  labelClassName?: string;
  // Validation messages, rendered under the matching control. Passed in rather
  // than derived here: the schema that decides whether these are required lives
  // with the form, and this control is used by forms that require them and by
  // forms that do not.
  stateError?: string;
  cityError?: string;
  disabled?: boolean;
}

/**
 * The standard State + City pair for the whole CRM.
 *
 * State is a fixed list (the 36 states/UTs); City is the bundled list for that
 * state plus anything an RM has added, and typing a new one persists it for
 * next time. Both are dropdowns everywhere on purpose: free text produced
 * "Bangalore"/"Bengaluru"/"bangalore" as three different cities, which is
 * unfixable once it is in the data and makes every geography rollup wrong.
 *
 * Lives in `components/common` because it is no longer a wizard control -
 * `components/sales/wizard/StateCitySelect.tsx` is a re-export shim.
 */
export const StateCitySelect = ({
  state,
  city,
  onStateChange,
  onCityChange,
  portalled = true,
  stateClassName,
  cityClassName,
  labelClassName,
  stateError,
  cityError,
  disabled,
}: StateCitySelectProps) => {
  const { toast } = useToast();
  const { data: customCities = {} } = useCustomCities(!!state);
  const addCity = useAddCustomCity();

  // Tolerate a state value that isn't a canonical key (e.g. an auto-populated
  // GST/ARN value that didn't normalize) so it's shown and selectable, never
  // dropped as "No results".
  const stateOptions = useMemo(
    () => (state && !INDIAN_STATES.includes(state) ? [state, ...INDIAN_STATES] : INDIAN_STATES),
    [state],
  );

  // Bundled cities + RM-added cities for this state, deduped case-insensitively
  // (the bundled spelling wins) so "Pune" and a custom "pune" don't both show.
  const cityOptions = useMemo(() => {
    if (!state) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    [...(CITIES_BY_STATE[state] ?? []), ...(customCities[state] ?? [])].forEach((name) => {
      const key = name.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(name);
      }
    });
    return out.sort();
  }, [state, customCities]);

  const handleStateChange = (next: string): void => {
    onStateChange(next);
    // Switching state invalidates the previously selected city.
    if (next !== state) onCityChange('');
  };

  // Persist the typed city so it appears in this state's dropdown next time.
  const handleCreateCity = (next: string): void => {
    if (!state) return;
    addCity.mutate(
      { state, city: next },
      {
        onError: () =>
          toast.error(
            'Could not save the new city. It is set for this lead but may not appear next time.',
          ),
      },
    );
  };

  const labelClass = labelClassName ?? 'text-xs text-muted-foreground';

  return (
    <>
      <div className={cn('grid gap-1.5', stateClassName)}>
        <Label className={labelClass}>State</Label>
        <Combobox
          value={state}
          onChange={handleStateChange}
          options={stateOptions}
          placeholder="Select state…"
          portalled={portalled}
          disabled={disabled}
        />
        {stateError && (
          <p className="text-xs text-destructive" role="alert">
            {stateError}
          </p>
        )}
      </div>
      <div className={cn('grid gap-1.5', cityClassName)}>
        <Label className={labelClass}>City</Label>
        <Combobox
          value={city}
          onChange={onCityChange}
          options={cityOptions}
          placeholder={state ? 'Select or type city…' : 'Select a state first'}
          disabled={disabled || !state}
          onCreate={handleCreateCity}
          portalled={portalled}
        />
        {cityError && (
          <p className="text-xs text-destructive" role="alert">
            {cityError}
          </p>
        )}
      </div>
    </>
  );
};
